
import { DescriptorKeyType, DynamicFormPartType } from "../Types";
import BaseViewModel from "./BaseViewModel";
import BoolControlViewModel from "./BoolControlViewModel";
import CollectionViewModel from "./CollectionViewModel";
import CurrentUserViewModel from "./CurrentUserViewModel";
import CustomerSelectViewModel from "./CustomerSelectViewModel";
import DateControlViewModel from "./DateControlViewModel";
import DictionaryRegistryControlViewModel from "./DictionaryRegistryControlViewModel";
import EnumControlViewModel from "./EnumControlViewModel";
import FiasRegionSelectControlViewModel from "./FiasRegionSelectControlViewModel";
import FloatControlViewModel from "./FloatControlViewModel";
import FormViewModel from "./FormViewModel";
import IntegerControlViewModel from "./IntegerControlViewModel";
import PageContainerViewModel from "./PageContainerViewModel";
import PageViewModel from "./PageViewModel";
import PlaceholderViewModel from "./PlaceholderViewModel";
import SimpleAttachmentViewModel from "./SimpleAttachmentViewModel";
import TextControlViewModel from "./TextControlViewModel";
import TitleControlViewModel from "./TitleControlViewModel";
import UserInfoListViewModel from "./UserInfoListViewModel";
import UuidControlViewModel from "./UuidControlViewModel";
import { getValueByPath, parseJsonIfString, resolveContextValueByPaths } from "../../../Utils/ContextUtils";

/**  */
export const constructors = {
	Placeholder: PlaceholderViewModel,
	FormDescriptor: FormViewModel,
	TextControlDescriptor: TextControlViewModel,
	EnumControlDescriptor: EnumControlViewModel,
	BoolControlDescriptor: BoolControlViewModel,
	DateControlDescriptor: DateControlViewModel,
	IntegerControlDescriptor: IntegerControlViewModel,
	FloatControlDescriptor: FloatControlViewModel,
	UuidControlDescriptor: UuidControlViewModel,
	DictionaryRegistryControlDescriptor: DictionaryRegistryControlViewModel,
	SimpleAttachmentViewModel: SimpleAttachmentViewModel,
	CurrentUserViewModel: CurrentUserViewModel,
	TitleControlDescriptor: TitleControlViewModel,
	PageDescriptor: PageViewModel,
	PageContainerControlDescriptor: PageContainerViewModel,
	FiasRegionSelectControlViewModel: FiasRegionSelectControlViewModel,
	FIAS: FiasRegionSelectControlViewModel,
	UserInfoList: UserInfoListViewModel,
	CustomerSelect: CustomerSelectViewModel,
	CollectionFormDescriptor: CollectionViewModel
};

/**
 * Функция генерирует модель формы
 * @param project Описание формы
 * @param dataContext контекстные данные формы
 * @returns Модель формы
 */
export const buildViewModel = ( project: DynamicFormPartType, dataContext: any ): FormViewModel => {
	BaseViewModel.isClientScripts = project.IsClientScripts;
	// Конструктор вью-модели формы.
	const FormViewModel = constructors.FormDescriptor;
	//Для начала создадим viewModel формы, затем по ней построим разметку.
	var viewModelPath = '_';
	const formViewModel = new FormViewModel( { 
		elementDescriptor: project, 
		parent: null, 
		viewModelPath, 
		businessEntityPath: project.Path,
		formObject: dataContext
	} );
	//Добавляем разметку главной форме.
	project.viewModelPath = viewModelPath;
	// Запускаем вью-модель в рекурсию - пусть расширит себя детьми.
	recursion( formViewModel, project, dataContext, "FormElements", false );
	return formViewModel;
};

const recursion = ( viewModel: BaseViewModel, formObjectInstance: DynamicFormPartType, dataContext: any, blockToAdd: string, itsApage: boolean = false ) => {
	
	if ( formObjectInstance.DescriptorKey === DescriptorKeyType.Placeholder ) return;

	// Формы (Form)
	const formObjectChildren = formObjectInstance.FormElements;
	// Если есть этот элемент, значит объект - форма.
	if ( !!formObjectChildren?.length ) {
		const length = formObjectChildren.length;
		// let currentMarkupId = null;
		for ( let index = 0; index < length; index++ ) {
			const childFormObject = formObjectChildren[index];
			// Создаем путь до корневого объекта viewModel.
			const viewModelPath = `${blockToAdd}[${index}]`;
			// Для FormDescriptor передаем в formObject соответствующий кусок контекста
			let entityValue = dataContext
				? resolveContextValueByPaths(dataContext, childFormObject.LocalPath, childFormObject.Path)
				: null;
			if ( entityValue === undefined && childFormObject.Path ) {
				entityValue = getValueByPath(BaseViewModel.dataContext, childFormObject.Path);
			}
			// Создаем вью-моделину ребенка.
			let ChildViewModel = constructors[childFormObject.DescriptorKey];
			if ( !ChildViewModel ) {
				ChildViewModel = constructors.Placeholder;
			}
			const childViewModel: BaseViewModel = new ChildViewModel({
				elementDescriptor: childFormObject,
				parent: viewModel,
				viewModelPath: viewModelPath,
				businessEntityPath: childFormObject.Path,
				formObject: childFormObject.DescriptorKey === "FormDescriptor" ? (entityValue ?? {}) : undefined
			}) as BaseViewModel;

			// Для внешних справочников проставляем идентификатор
			if ( childFormObject.DescriptorKey === "DictionaryRegistryControlDescriptor" ) {
				const child = childViewModel as DictionaryRegistryControlViewModel;
				child.DictionaryId = childFormObject.DictionaryId;
				child.DictionarySource = childFormObject.DictionarySource;
				child.DisplayFieldScript = childFormObject.DisplayFieldScript;
				child.ParentId = childFormObject.ParentId;

				if ( BaseViewModel.isClientScripts !== false )
					child.fillGetParentIdScript();
			}

			// Её разметка.
			childFormObject.viewModelPath = viewModelPath;

			// Добавляем в коллекцию элементов формы-родителя.
			if ( itsApage === true ) {
				const page = viewModel as PageViewModel;
				page.PageElements.push(childViewModel);
			} 
			else {
				const frm = viewModel as FormViewModel;
				frm.FormElements.push(childViewModel);
			}

			// Заполняем значением, если оно найдено в контексте
			if ( entityValue !== undefined && entityValue !== null ) {
				childViewModel.setValue( entityValue );
			}
			if ( childViewModel?.DescriptorKey === "SimpleAttachmentViewModel" ) {
				const rawKey: string = childViewModel.FullPath || childViewModel.LocalPath;
				const key = rawKey?.startsWith("_.") ? rawKey.substring(2) : rawKey;
				const storedFileName = BaseViewModel.formContextData?.[key];
				if ( typeof storedFileName === "string" && storedFileName ) {
					childViewModel.setValue( storedFileName );
				}
			}

			if (childViewModel.DescriptorKey === "UserInfoList") {
				childViewModel.setValue(entityValue);
			}

			// Запускаем еще одну итерацию рекурсии (вдруг и у дитя есть дети).
			recursion( childViewModel, childFormObject, entityValue ?? dataContext, "FormElements", false );
		}
	}
	else if ( !!formObjectInstance.CollectionElementDescriptor ) {
		const model = viewModel as CollectionViewModel;
		model.ButtonCaption = formObjectInstance.ButtonCaption ?? "Добавить";
		// Здесь нужно проанализировать коллекции.
		const formElementsC = formObjectInstance?.CollectionElementDescriptor?.FormElements ?? [];
		if ( formElementsC.length > 0 ) {
			// перед повторным наполнением коллекции — очистить текущие элементы, чтобы избежать дублей
			if ( Array.isArray(model.CollectionItems) && model.CollectionItems.length ) {
				model.CollectionItems.length = 0;
			}
			// label кнопки
			// В коллекции есть дети, их и обработаем
			// Сам клонируемый элемент — это форма
			var FormViewModel = constructors['FormDescriptor'];
			// itemToAdd — образец клонируемого элемента, соберём его
			// это хрень для того чтобы реально ещё несуществующие пути не попали в словарь путей
			model.itemToAdd = new FormViewModel({
				elementDescriptor: formObjectInstance.CollectionElementDescriptor!, 
				parent: model, 
				viewModelPath: formObjectInstance?.CollectionElementDescriptor?.Path!, 
				businessEntityPath: formObjectInstance.Path  
			});
			// Перебираем все элементы на форме
			for ( let index = 0; index < formElementsC.length; index++ ) {
				// в цикле всех перебрать, добавить как детей к форме, а форму добавить в шаблон элемента коллекции
				const childFormObject1 = formElementsC[index];
				// Создаем путь до корневого объекта viewModel.
				const viewModelPath1 = `${blockToAdd}[${index}]`;
				// Создаем вью-моделину ребенка.
				let ChildViewModel1 = constructors[childFormObject1.DescriptorKey];
				if ( !ChildViewModel1 ) {
					ChildViewModel1 = constructors['Placeholder'];
				}
				const childViewModel1 = new ChildViewModel1({
					elementDescriptor: childFormObject1, 
					parent: model.itemToAdd, 
					viewModelPath: viewModelPath1, 
					businessEntityPath: childFormObject1.Path 
				});

				// Для внешних справочников проставляем идентификатор
				if ( childFormObject1.DescriptorKey === "DictionaryRegistryControlDescriptor" ) {
					const dictChild = childViewModel1 as DictionaryRegistryControlViewModel;
					dictChild.DictionaryId = childFormObject1.DictionaryId;
					dictChild.DictionarySource = childFormObject1.DictionarySource;
					dictChild.DisplayFieldScript = childFormObject1.DisplayFieldScript;
					dictChild.ParentId = childFormObject1.ParentId;
					if ( BaseViewModel.isClientScripts !== false )
						dictChild.fillGetParentIdScript();
				}
				// Запускаем еще одну итерацию рекурсии (вдруг и у дитя есть дети).
				recursion(childViewModel1, childFormObject1, dataContext, blockToAdd, false );
				// добавляем в клонируемый элемент ребёнка
				model.itemToAdd.FormElements.push(childViewModel1);
			}
			const rawCollectionData = Array.isArray(dataContext)
				? dataContext
				: resolveContextValueByPaths(dataContext, formObjectInstance.LocalPath, formObjectInstance.Path);
			let normalizedCollectionData = parseJsonIfString(rawCollectionData);
			let collectionItems = Array.isArray(normalizedCollectionData) ? normalizedCollectionData : [];
			if ( collectionItems.length === 0 ) {
				const fromRoot = getValueByPath(BaseViewModel.dataContext, model.FullPath);
				normalizedCollectionData = parseJsonIfString(fromRoot);
				collectionItems = Array.isArray(normalizedCollectionData) ? normalizedCollectionData : [];
			}

			//добавим для "обязательной" коллекции, только если нет данных
			const hasDataContextItems = collectionItems.length > 0;
			if ( !hasDataContextItems && model.IsMandatory ) {
				model.addItem( 0 );
			}
			if ( collectionItems.length ) {
				collectionItems.forEach( ( data, index ) => {
					const item = model.addItem(  index, data );
					model.setValueForChild( item, data );
				});
			}
		}
	}
	else if ( !!formObjectInstance.Pages ) {
		// Вьюмодель контейнера для вкладок
		const containerViewModel = new constructors[formObjectInstance.DescriptorKey]({
			elementDescriptor: formObjectInstance, 
			parent: viewModel,
			viewModelPath: formObjectInstance.Path, 
			businessEntityPath: formObjectInstance.Path
		}) as PageContainerViewModel;

		const pagesSize = formObjectInstance.Pages?.length ?? 0;
		for ( let pageIndex = 0; pageIndex < pagesSize; pageIndex++ ) {
			const pageObject = formObjectInstance.Pages[pageIndex];
			const pageViewModelPath = `Pages[${pageIndex}]`;
			const Constructor = constructors[pageObject.DescriptorKey];
			// Создаем вью-модель ребенка из шаблону
			const childInstViewModel = new Constructor({
				elementDescriptor: pageObject, 
				parent: containerViewModel, 
				viewModelPath: pageViewModelPath, 
				businessEntityPath: pageObject.Path 
			}) as PageViewModel;
			childInstViewModel.PageIndex = pageIndex;
			// Конкретный индекс вьюмодели порядковый
			pageObject.viewModelPath = pageViewModelPath;

			let entityValue = null;
			if ( dataContext ) {
				entityValue = resolveContextValueByPaths(dataContext, childInstViewModel.LocalPath, childInstViewModel.Path);
				if ( entityValue === undefined && childInstViewModel.Path ) {
					entityValue = getValueByPath(BaseViewModel.dataContext, childInstViewModel.Path);
				}
				if ( entityValue !== undefined && entityValue !== null ) {
					childInstViewModel.setValue( entityValue );
				}
			}

			recursion( childInstViewModel, pageObject, dataContext, "PageElements", true);

			(viewModel as any).Pages.push( childInstViewModel );
		}
	}
};

export { 
	BaseViewModel, FormViewModel, BoolControlViewModel, CollectionViewModel, CurrentUserViewModel, CustomerSelectViewModel,
	DateControlViewModel, DictionaryRegistryControlViewModel, EnumControlViewModel, FiasRegionSelectControlViewModel,
	IntegerControlViewModel, PageContainerViewModel, PageViewModel, PlaceholderViewModel, SimpleAttachmentViewModel,
	TextControlViewModel, TitleControlViewModel, UserInfoListViewModel, UuidControlViewModel, FloatControlViewModel
};

export default buildViewModel;
